import { nanoid } from 'nanoid';

import {
  WithdrawToWalletRequestFailed,
  WithdrawToWalletRequestSent,
  InitiateWithdrawRequestFailed,
  InitiateWithdrawRequestInProgress,
  InitiateWithdrawRequestSent,
  WithdrawRequestReady,
  WithdrawSuccessful,
  WithdrawUnexpectedError,
} from '#/components/common/notifications/withdraw';
import {
  CancelledLimitOrder,
  CancelledMarketOrder,
  FilledMarketOrder,
  SubmittedOrder,
} from '#/components/common/notifications/orders';
import { LimitOrderFill } from '#/components/common/notifications/fills';
import { TradeBusted } from '#/components/common/notifications/trade-busts';
import {
  AccountIsBeingLiquidated,
  AccountWasLiquidated,
} from '#/components/common/notifications/account';
import {
  DepositFailed,
  DepositToParaclearInProgress,
  DepositRequestReady,
  DepositRequestSent,
  DepositSuccessful,
  DepositUnexpectedError,
} from '#/components/common/notifications/deposit';
import { ToastOptions } from '#/components/common/toast';

import { NotificationEntryType, NotificationPropsByType } from './types';

type NotificationsMap = {
  [Type in NotificationEntryType]: {
    readonly getId: (
      kind: Type,
      props: NotificationPropsByType[Type],
    ) => string;
    readonly Component: React.ComponentType<NotificationPropsByType[Type]>;
    readonly toastOptions: ToastOptions;
  };
};

export const NOTIFICATIONS_MAP: NotificationsMap = {
  'deposit-successful': {
    getId: (kind, props) => `${kind}-${props.id}`,
    Component: DepositSuccessful,
    toastOptions: {},
  },
  'deposit-failed': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: DepositFailed,
    toastOptions: {},
  },
  'deposit-unexpected-error': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: DepositUnexpectedError,
    toastOptions: {},
  },
  'deposit-request-sent': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: DepositRequestSent,
    toastOptions: {},
  },
  'deposit-request-ready': {
    getId: (kind, props) => `${kind}-${props.id}`,
    Component: DepositRequestReady,
    toastOptions: { autoClose: false },
  },
  'deposit-to-paraclear-in-progress': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: DepositToParaclearInProgress,
    toastOptions: {},
  },
  'withdraw-unexpected-error': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: WithdrawUnexpectedError,
    toastOptions: {},
  },
  'initiate-withdraw-request-sent': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: InitiateWithdrawRequestSent,
    toastOptions: {},
  },
  'initiate-withdraw-request-in-progress': {
    getId: (kind, props) => `${kind}-${props.id}`,
    Component: InitiateWithdrawRequestInProgress,
    toastOptions: {},
  },
  'initiate-withdraw-request-failed': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: InitiateWithdrawRequestFailed,
    toastOptions: {},
  },
  'withdraw-request-ready': {
    getId: (kind, props) => `${kind}-${props.id}`,
    Component: WithdrawRequestReady,
    toastOptions: { autoClose: false },
  },
  'withdraw-to-wallet-request-sent': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: WithdrawToWalletRequestSent,
    toastOptions: {},
  },
  'withdraw-to-wallet-request-failed': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: WithdrawToWalletRequestFailed,
    toastOptions: {},
  },
  'withdraw-successful': {
    getId: (kind, props) => `${kind}-${props.id}`,
    Component: WithdrawSuccessful,
    toastOptions: {},
  },
  'filled-market-order': {
    getId: (kind, props) => `${kind}-${props.order.id}`,
    Component: FilledMarketOrder,
    toastOptions: {},
  },
  'filled-market-fill': {
    getId: (kind, props) => `${kind}-${props.fill.id}`,
    Component: LimitOrderFill,
    toastOptions: {},
  },
  'trade-bust': {
    getId: (kind, props) => `${kind}-${props.bust.busted_fill_id}`,
    Component: TradeBusted,
    toastOptions: { autoClose: false },
  },
  'account-is-being-liquidated': {
    getId: (kind, _props) => `${kind}-${nanoid()}`,
    Component: AccountIsBeingLiquidated,
    toastOptions: {},
  },
  'account-was-liquidated': {
    getId: (kind, props) => `${kind}-${props.liquidation.id}`,
    Component: AccountWasLiquidated,
    toastOptions: { autoClose: false },
  },
  'cancelled-market-order': {
    getId: (kind, props) => `${kind}-${props.order.id}`,
    Component: CancelledMarketOrder,
    toastOptions: {},
  },
  'submitted-order': {
    getId: (kind, props) => `${kind}-${props.order.id}`,
    Component: SubmittedOrder,
    toastOptions: {},
  },
  'cancelled-limit-order': {
    getId: (kind, props) => `${kind}-${props.order.id}`,
    Component: CancelledLimitOrder,
    toastOptions: {},
  },
};
